import ReactGA from "react-ga4";
import { getRegion } from "@util/domain";

// Google Analytics per region: the AU hub keeps its existing ID;
// the International hub reports to the .co property
const GA_MEASUREMENT_IDS = { au: "G-NF2673GH79", global: "G-ZDZJJT6JZ5" };
const CONSENT_KEY = "fioh-analytics-consent";
export const CONSENT_SETTINGS_EVENT = "fioh:consent-settings";

export type AnalyticsConsent = "granted" | "denied";

let initialised = false;

const measurementId = () => GA_MEASUREMENT_IDS[getRegion()];

// International visitors are asked before Analytics starts (UK and EU cookie rules).
// The AU hub is unchanged.
export const needsAnalyticsConsent = () => getRegion() === "global";

export const getAnalyticsConsent = (): AnalyticsConsent | null => {
    try {
        const value = window.localStorage.getItem(CONSENT_KEY);
        return value === "granted" || value === "denied" ? value : null;
    } catch {
        return null; // Storage blocked: treat as not yet answered
    }
};

// Removes Google Analytics cookies (_ga, _ga_*) from this host and its parent domains
const clearAnalyticsCookies = () => {
    const names = document.cookie.split(";")
        .map((c) => c.split("=")[0].trim())
        .filter((n) => n === "_ga" || n.startsWith("_ga_"));
    const parts = window.location.hostname.split(".");
    const domains = parts.map((_, i) => parts.slice(i).join(".")).filter((d) => d.includes("."));
    names.forEach((name) => {
        document.cookie = `${name}=; Max-Age=0; path=/`;
        domains.forEach((domain) => {
            document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}`;
        });
    });
};

export const setAnalyticsConsent = (value: AnalyticsConsent) => {
    try {
        window.localStorage.setItem(CONSENT_KEY, value);
    } catch {
        // Storage blocked: the choice applies to this page view only
    }
    // Google's documented opt-out flag stops any further hits if Analytics
    // was already running on this page
    const flags = window as unknown as Record<string, boolean>;
    flags[`ga-disable-${measurementId()}`] = value === "denied";
    if (value === "denied") clearAnalyticsCookies();
};

// Reopens the consent bar (footer "Cookie Settings" link) so a visitor can change their choice
export const openConsentSettings = () => {
    window.dispatchEvent(new Event(CONSENT_SETTINGS_EVENT));
};

// Starts Analytics unless on localhost, on the private tribute management page,
// or (International only) before the visitor has accepted
export const initGA = (pathname: string = window.location.pathname) => {
    const { hostname } = window.location;
    const isLocalhost = hostname === "localhost" || hostname.endsWith(".localhost");
    if (initialised || isLocalhost || /^\/tribute\/manage\/?$/i.test(pathname)) return;
    if (needsAnalyticsConsent() && getAnalyticsConsent() !== "granted") return;
    ReactGA.initialize(measurementId());
    initialised = true;
};

// Nothing is sent or queued before Analytics has started
export const trackPageView = (page: string) => {
    if (!initialised) return;
    ReactGA.send({ hitType: "pageview", page });
};

export const trackEvent = (name: string, params?: Record<string, unknown>) => {
    if (!initialised) return;
    ReactGA.event(name, params);
};
